"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Button from "../../../../components/Button";
import FilterButtons from "../../../../components/universal-filter-tabs";
import OverlayModal from "../../../../components/Modal";
import SmallerSpinner from "../../../../components/SmallerSpinner";
import styles from "../css/frontShopLanding.module.css";
import { LuShoppingBasket, LuBadgeCheck, LuBan, LuTrash2 } from "react-icons/lu";
import { search as apiSearch, warmCache as apiWarmCache } from "../../../../api/frontShop/SearchAPI";
import { getCart as apiGetCart, updateCartItem as apiUpdateCartItem, addToCart as apiAddToCart, clearSelectedCustomer as apiClearSelectedCustomer, getSelectedCustomer as apiGetSelectedCustomer, loadUnpaidOrderToCart as apiLoadUnpaidOrderToCart, selectCustomer as apiSelectCustomer, getCustomerDetails as apiGetCustomerDetails } from "../../../../api/frontShop/CheckOutAPI";
import { FiSearch } from "react-icons/fi";
import { lookupReceipt as apiLookupReceipt } from "../../../../api/frontShop/ReceiptAPI";

export default function FrontShopLanding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [customer, setCustomer] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const scanInputRef = useRef(null);
  const [scanValue, setScanValue] = useState("");
  const scanBufferRef = useRef("");
  const scanLastTsRef = useRef(0);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const q = query.trim();
    let alive = true;
    if (!q) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      apiSearch(q)
        .then((res) => { if (alive) setResults(Array.isArray(res.data) ? res.data : []); })
        .catch(() => { if (alive) setResults([]); })
        .finally(() => { if (alive) setLoading(false); });
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [query]);

  
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);
  const [updatingSku, setUpdatingSku] = useState(null);

  async function updateQty(sku, qty) {
    try {
      setUpdatingSku(sku);
      const { data: dResp } = await apiUpdateCartItem({ sku, qty });
      const d = dResp || {};
      setBasket(Array.isArray(d.items) ? d.items : []);
      setSubtotal(Number(d.subtotal || 0));
      setTax(Number(d.tax || 0));
      setTotal(Number(d.total || 0));
    } finally {
      setUpdatingSku(null);
    }
  }

  async function addSkuToCart(sku) {
    try {
      await apiAddToCart({ sku, qty: 1 });
      await refreshCart();
    } catch (e) {
      let msg = "Failed to add to cart";
      if (e?.message) msg = e.message;
      alert(msg);
    }
  }

  // Server-backed basket
  const [basket, setBasket] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [tax, setTax] = useState(0);
  const [total, setTotal] = useState(0);
  async function refreshCart() {
    try {
      const { data: d } = await apiGetCart();
      setBasket(Array.isArray(d.items) ? d.items : []);
      setSubtotal(Number(d.subtotal || 0));
      setTax(Number(d.tax || 0));
      setTotal(Number(d.total || 0));
    } catch {}
  }
  useEffect(() => { refreshCart(); }, []);

  // Edit unpaid order mode: load the order's items into the cart
  useEffect(() => {
    const rec = location.state && typeof location.state.editRecieptno === "string" ? location.state.editRecieptno : "";
    const editing = !!(location.state && location.state.editUnpaidOrder);
    if (!editing || !rec) return;
    (async () => {
      try {
        await apiLoadUnpaidOrderToCart(rec);
      } catch {}
      await refreshCart();
    })();
  }, [location.state]);
  // Warm search cache once when landing opens
  useEffect(() => { apiWarmCache().catch(() => {}); }, []);
  

  

  // Load selected customer only when returning from Add Customer
  useEffect(() => {
    try {
      if (location.state?.selectedCustomer) {
        const raw = localStorage.getItem("frontshop_customer");
        if (raw) setCustomer(JSON.parse(raw));
      }
    } catch {}
  }, [location.state]);

  // Fallback: hydrate from backend session if localStorage empty
  useEffect(() => {
    let cancelled = false;
    async function hydrate() {
      try {
        if (customer) return; // already set
        const { data: json } = await apiGetSelectedCustomer();
        if (cancelled) return;
        if (json && json.patientid && (json.surname || json.initial || json.title)) {
          setCustomer({
            surname: json.surname || '',
            initial: json.initial || '',
            title: json.title || '',
            role: json.role || '',
          });
        }
      } catch {}
    }
    hydrate();
    return () => { cancelled = true; };
  }, [customer]);

  async function handleScanSubmit(rawCode) {
    const code = String(rawCode ?? scanValue).trim();
    if (!code) return;
    try {
      const { data: json } = await apiLookupReceipt(code);
      const info = json?.data;
      const pidRaw = info?.patientid;
      const patientIdFromReceipt = Number(pidRaw);
      if (Number.isFinite(patientIdFromReceipt) && patientIdFromReceipt > 0) {
        try {
          await apiSelectCustomer(patientIdFromReceipt);
          const { data: details } = await apiGetCustomerDetails(patientIdFromReceipt);
          const main = details?.main || null;
          const payload = {
            patientid: patientIdFromReceipt,
            surname: main?.surname || "",
            initial: (main?.firstname || "").slice(0, 1).toUpperCase(),
            title: main?.title || "",
            role: "Main Member",
          };
          try { localStorage.setItem("frontshop_customer", JSON.stringify(payload)); } catch {}
          setCustomer(payload);
        } catch {}
      }
      if (info && typeof info.orderid !== "undefined") {
        if (info.completed) {
          const method = String(info.paymentMethod || "Card").trim().toLowerCase();
          const rec = code; // we scanned the reciept number
          if (method === "cash") {
            navigate("/frontshop/orders/cash", { state: { recieptno: rec } });
          } else if (method === "card" || method === "nedbank" || method === "ikhokha") {
            const display = method === "ikhokha" ? "Ikhokha" : method === "nedbank" ? "Nedbank" : "Card";
            navigate("/frontshop/orders/card", { state: { paymentMethod: display, recieptno: rec } });
          } else if (method === "cod") {
            navigate("/frontshop/orders/cod", { state: { recieptno: rec } });
          } else if (method === "credit") {
            navigate("/frontshop/orders/credit", { state: { recieptno: rec } });
          } else if (method === "medical aid" || method === "medical-aid") {
            navigate("/frontshop/orders/medical-aid", { state: { recieptno: rec } });
          } else if (method === "split payment" || method === "split payments" || method === "split") {
            navigate("/frontshop/orders/split", { state: { recieptno: rec } });
          } else {
            navigate("/frontshop/orders/card", { state: { paymentMethod: "Card", recieptno: rec } });
          }
        } else {
          const optionIds = Array.isArray(info?.optionid) ? info.optionid.map(String) : [];
          const isCollection = !!info?.requiresCheckout || optionIds.includes("COLL");

          const rawTotalDue = info?.totalDue;
          const totalDue = Number.isFinite(Number(rawTotalDue)) && Number(rawTotalDue) > 0
            ? Number(rawTotalDue)
            : (() => {
                const optArr = Array.isArray(info?.optionamount) ? info.optionamount : [];
                const opt0 = Number(optArr[0]);
                if (Number.isFinite(opt0) && opt0 > 0) return opt0;
                const tot = Number(info?.totalamount);
                return Number.isFinite(tot) && tot > 0 ? tot : undefined;
              })();

          if (isCollection) {
            await apiLoadUnpaidOrderToCart(code);
            await refreshCart();
            return;
          } else {
            // Not completed COD: go to COD payment for that order
            navigate("/frontshop/payment-types/cod/payment", {
              state: {
                orderId: info.orderid,
                recieptno: code,
                ...(typeof totalDue === "number" ? { total: totalDue } : {}),
              },
            });
          }
        }
      }
    } catch (e) {
      // ignore lookup failures silently for now
    } finally {
      setScanValue("");
      // re-focus hidden input to continue scanning
      setTimeout(() => scanInputRef.current && scanInputRef.current.focus(), 0);
    }
  }

  useEffect(() => {
    // keep hidden scan input focused
    if (scanInputRef.current) scanInputRef.current.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const key = e.key;
      const now = Date.now();

      // reset buffer if user is typing slowly
      if (now - (scanLastTsRef.current || 0) > 120) {
        scanBufferRef.current = "";
      }
      scanLastTsRef.current = now;

      if (key === "Enter") {
        const code = String(scanBufferRef.current || "").trim();
        scanBufferRef.current = "";
        if (code) {
          e.preventDefault();
          e.stopPropagation();
          setScanValue("");
          handleScanSubmit(code);
        }
        return;
      }

      // Accept common barcode characters (digits/letters)
      if (key && key.length === 1) {
        scanBufferRef.current += key;
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);

  useEffect(() => {
    // re-arm scanner when returning to this page
    setScanValue("");
    setTimeout(() => scanInputRef.current && scanInputRef.current.focus(), 0);
  }, [location.pathname]);

  return (
    <div className={styles.wrapper}>
      <Navbar />

      <div className={styles.container}>
        <div className={styles.topBar}>
          <FilterButtons
            buttons={["Payment", "Debtors", "Completed Transactions"]}
            activeButton={"Payment"}
            onButtonClick={(label) => {
              if (label === "Debtors") {
                navigate("/frontshop/debtors");
              } else if (label === "Completed Transactions") {
                navigate("/frontshop/orders");
              }
            }}
          />
        </div>

        <div className={styles.layout}>
          <section className={styles.searchArea}>
            <div className={styles.searchWrap}>
              <FiSearch className={styles.searchIcon} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className={styles.searchInput}
                placeholder="Search product, generics, or keywords..."
              />
              {/* Hidden input captures scanner keystrokes and Enter */}
              <input
                ref={scanInputRef}
                value={scanValue}
                onChange={(e) => setScanValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleScanSubmit();
                  }
                }}
                autoFocus
                inputMode="numeric"
                aria-hidden="true"
                style={{ position: "absolute", opacity: 0, width: 1, height: 1, pointerEvents: "none" }}
                tabIndex={-1}
              />
            </div>

            {loading && <div className={styles.resultsList}>Loading...</div>}
            {!loading && query.trim() && results.length === 0 && (
              <div className={styles.resultsList} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
                No Results
              </div>
            )}
            {results.length > 0 && !loading && (
              <div className={styles.resultsList}>
                {results.map((p) => (
                  <div key={p.name} className={styles.productCard}>
                    <div className={styles.productHeader}>
                      <div className={styles.productTitle}>{p.name}</div>
                      <button
                        className={styles.optionsLink}
                        onClick={() => {
                          const variants = Array.isArray(p.variants) ? p.variants : [];
                          const firstAvailable = variants.find((v) => v?.available);
                          const chosen = firstAvailable || variants[0];
                          if (chosen?.sku && chosen?.available) addSkuToCart(chosen.sku);
                        }}
                      >
                        {p.variants?.length || 0} Options
                      </button>
                      <div className={styles.categoryTag}>{p.category}</div>
                    </div>
                    <div className={styles.metaRow}>
                      <span>{p.activeingredient}</span>
                      <span>Shelve:{p.shelf ?? "-"}</span>
                    </div>
                    <div className={styles.cardDivider} />
                    <div className={styles.optionList}>
                      {(p.variants || []).map((o) => (
                        <button
                          key={o.sku}
                          className={`${styles.optionRow} ${!o.available ? styles.optionDisabled : ""}`}
                          onClick={() => {
                            if (!o.available) return;
                            addSkuToCart(o.sku);
                          }}
                        >
                          <div className={styles.optionLeft}>
                            <div className={styles.optionName}>{o.label}</div>
                            <div className={styles.optionBadges}>
                              <span className={`${styles.badge} ${o.available ? styles.badgeOk : styles.badgeError}`}>
                                {o.available ? (
                                  <>
                                    <LuBadgeCheck className={styles.badgeIcon} />
                                    Available
                                  </>
                                ) : (
                                  <>
                                    <LuBan className={styles.badgeIcon} />
                                    Sold out
                                  </>
                                )}
                              </span>
                              <span className={styles.badgeMuted}>{o.left} Left</span>
                            </div>
                          </div>
                          <div className={styles.optionPrice}>R {Number(o.price || 0).toFixed(2)}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <aside className={styles.basket}>
            <div className={styles.basketHeader}>Basket</div>
            <div className={styles.itemsTotal}>{basket.reduce((s, x) => s + (x.qty || 0), 0)} Items Total</div>
            <div className={styles.divider} />

            {customer ? (
              <div className={styles.customerCard}>
                <div className={styles.customerCardHeader}>Customer</div>
                <div className={styles.customerCardTopRow}>
                  <div className={styles.customerCardName}>
                    {customer.title} {customer.surname} ({customer.initial})
                  </div>
                  <div className={styles.customerCardActions}>
                    <Button variant="primary" onClick={() => navigate('/frontshop/debtors/credit-accounts')}>Change</Button>
                    <Button variant="secondary" onClick={async () => {
                      try { await apiClearSelectedCustomer(); } catch {}
                      try { localStorage.removeItem('frontshop_customer'); } catch {}
                      setCustomer(null);
                    }}>Remove</Button>
                  </div>
                </div>
                <div className={styles.customerCardRole}>{customer.role}</div>
              </div>
            ) : null}

            {basket.length === 0 ? (
              <div className={styles.emptyState}>
                <LuShoppingBasket className={styles.emptyIcon} />
                <div className={styles.emptyTitle}>Cart is empty</div>
                <div className={styles.emptySubtitle}>Select item from list</div>
              </div>
            ) : (
              <div className={styles.basketList}>
                {basket.map((it) => (
                  <div key={it.sku} className={styles.basketItem}>
                    <div className={styles.basketItemMain}>
                      <div className={styles.basketItemTitle}>{it.name || it.sku}</div>
                      <div className={styles.basketItemUnit}>Unit: R {Number(it.price || 0).toFixed(2)}</div>
                    </div>
                    <div className={styles.basketItemActions}>
                      <SmallerSpinner
                        value={Number(it.qty || 1)}
                        min={0}
                        step={1}
                        ariaLabel={`Quantity for ${it.name || it.sku}`}
                        onChange={(next) => updateQty(it.sku, Number(next))}
                        className={styles.qtySpinner}
                        baseWidth={130}
                        style={{ transform: 'scale(1.0)', '--control-height': '34px' }}
                        btnStyle={{ padding: '2px 6px' }}
                        plusStyle={{ padding: '2px 6px' }}
                      />
                      <div className={styles.linePrice}>R {Number((it.price || 0) * (it.qty || 0)).toFixed(2)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className={styles.totals}>
              <div className={styles.totalRow}><span>Subtotal</span><span>R {Number(subtotal).toFixed(2)}</span></div>
              <div className={styles.totalRow}><span>Tax (15%)</span><span>R {Number(tax).toFixed(2)}</span></div>
              <div className={styles.totalsDivider} />
              <div className={styles.totalRow}><span>Total Amount</span><span>R {Number(total).toFixed(2)}</span></div>

              <div className={styles.checkoutWrap}>
                <Button
                  variant="primary"
                  className={styles.checkoutBtn}
                  disabled={basket.length === 0}
                  onClick={() => {
                    const returnTo = location.state && typeof location.state.returnTo === "string" ? location.state.returnTo : "";
                    const editRec = location.state && typeof location.state.editRecieptno === "string" ? location.state.editRecieptno : "";
                    const paymentTitle = location.state && typeof location.state.paymentTitle === "string" ? location.state.paymentTitle : undefined;

                    if (returnTo && editRec) {
                      navigate(returnTo, {
                        state: {
                          recieptno: editRec,
                          total,
                          ...(paymentTitle ? { paymentTitle } : {}),
                        },
                      });
                      return;
                    }

                    try { localStorage.removeItem('frontshop_customer'); } catch {}
                    setCustomer(null);
                    navigate("/frontshop/payment-types", { state: { total } });
                  }}
                >
                  Checkout
                </Button>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <div className={styles.footerWrapper}>
        <Footer />
      </div>
      
      {confirmOpen && confirmItem && (
        <OverlayModal onClose={() => { setConfirmOpen(false); setConfirmItem(null); }} closeStyle="plain">
          <div className={styles.confirmModal}>
            <h3 className={styles.confirmTitle}>Remove item</h3>
            <p className={styles.confirmBody}>
              Are you sure you wish to delete "{confirmItem.name}"?
            </p>
            <div className={styles.confirmActions}>
              <Button
                variant="approve"
                className={styles.smallBtn}
                onClick={() => { setConfirmOpen(false); setConfirmItem(null); }}
              >
                Cancel
              </Button>
              <Button
                variant="cancel"
                onClick={() => { setConfirmOpen(false); setConfirmItem(null); }}
              >
                Delete
              </Button>
            </div>
          </div>
        </OverlayModal>
      )}
    </div>
  );
}

